import { getBootstrapTab } from './bootstrap-runtime';

/** Must match `FormRenderer::TAB_ERROR_CLASS`. */
export const FORM_TAB_ERROR_CLASS = 'nais-form__tab-link--error';

const PANE_ERROR_SELECTOR = [
    '.has-error',
    '.is-invalid',
    '.errors:not(:empty)',
    '.help-block.text-danger',
].join(', ');

const FOCUSABLE_ERROR_SELECTOR = [
    '.has-error input:not([type="hidden"]):not([disabled])',
    '.has-error select:not([disabled])',
    '.has-error textarea:not([disabled])',
    'input.has-error:not([type="hidden"]):not([disabled])',
    'select.has-error:not([disabled])',
    'textarea.has-error:not([disabled])',
    '.is-invalid input:not([type="hidden"]):not([disabled])',
    '.is-invalid select:not([disabled])',
    '.is-invalid textarea:not([disabled])',
    'input.is-invalid:not([type="hidden"]):not([disabled])',
    'select.is-invalid:not([disabled])',
    'textarea.is-invalid:not([disabled])',
].join(', ');

type NetteTabHost = {
    addError?: (el: HTMLElement, message: string) => void;
    validateForm?: (sender: HTMLFormElement | HTMLElement, onlyCheck?: boolean) => boolean;
    initForm?: (form: HTMLFormElement) => void;
};

type LiveFormTabHost = {
    addError?: (el: HTMLElement, message: string) => void;
    removeError?: (el: HTMLElement) => void;
};

function getSelect2Container(el: HTMLElement): HTMLElement | null {
    const sibling = el.nextElementSibling;
    return sibling instanceof HTMLElement && sibling.classList.contains('select2-container')
        ? sibling
        : null;
}

function placeSelect2ErrorMessage(el: HTMLElement): void {
    const select2 = getSelect2Container(el);
    const messageId = el.dataset.lfvMessageId;
    if (!select2 || !messageId) {
        return;
    }

    const message = document.getElementById(messageId);
    if (!message) {
        return;
    }

    const inputGroup = el.closest<HTMLElement>('.input-group');
    if (inputGroup) {
        inputGroup.insertAdjacentElement('afterend', message);
        return;
    }

    select2.insertAdjacentElement('afterend', message);
}

function syncControlErrorState(el: HTMLElement, invalid: boolean): void {
    el.classList.toggle('is-invalid', invalid);
    if (invalid) {
        el.setAttribute('aria-invalid', 'true');
    } else {
        el.removeAttribute('aria-invalid');
    }

    getSelect2Container(el)?.classList.toggle('is-invalid', invalid);

    const field = el.closest<HTMLElement>('[data-nais-field]');
    if (field) {
        field.classList.toggle('has-error', field.querySelector('.is-invalid') !== null);
    }
}

function paneHasError(pane: HTMLElement): boolean {
    return pane.querySelector(PANE_ERROR_SELECTOR) !== null;
}

function findTabLink(form: HTMLFormElement, pane: HTMLElement): HTMLElement | null {
    const paneId = pane.id;
    if (!paneId) {
        return null;
    }

    const escaped = CSS.escape(paneId);
    return form.querySelector<HTMLElement>(
        `[data-bs-toggle="tab"][href="#${escaped}"], [data-bs-toggle="tab"][aria-controls="${escaped}"]`,
    );
}

function focusFirstInvalidField(pane: HTMLElement): void {
    const field = pane.querySelector<HTMLElement>(FOCUSABLE_ERROR_SELECTOR);
    if (!field || typeof field.focus !== 'function') {
        return;
    }

    try {
        field.focus();
    } catch {
        /* hidden or detached after snippet replace */
    }
}

function getFormTabPanes(form: HTMLFormElement): HTMLElement[] {
    return Array.from(form.querySelectorAll<HTMLElement>('.tab-content > .tab-pane')).filter(
        (pane) => !pane.classList.contains('nais-datagrid-filter-pane')
            && pane.closest('.nais-datagrid-filter-tab-content') === null,
    );
}

function activateErrorTab(link: HTMLElement, pane: HTMLElement): void {
    const alreadyActive = link.classList.contains('active') || pane.classList.contains('active');
    if (alreadyActive) {
        focusFirstInvalidField(pane);
        return;
    }

    const onShown = (): void => {
        link.removeEventListener('shown.bs.tab', onShown);
        focusFirstInvalidField(pane);
    };
    link.addEventListener('shown.bs.tab', onShown);

    try {
        getBootstrapTab().getOrCreateInstance(link).show();
    } catch {
        link.removeEventListener('shown.bs.tab', onShown);
        focusFirstInvalidField(pane);
    }
}

/**
 * Marks tab links that contain invalid fields. When `activate` is set, switches
 * to the first invalid tab (client-side submit / server re-render).
 */
export function syncFormTabErrors(form: HTMLFormElement, options: { activate?: boolean } = {}): void {
    if (!(form instanceof HTMLFormElement)) {
        return;
    }

    const panes = getFormTabPanes(form);
    if (panes.length === 0) {
        return;
    }

    let firstErrorLink: HTMLElement | null = null;
    let firstErrorPane: HTMLElement | null = null;

    for (const pane of panes) {
        const link = findTabLink(form, pane);
        if (!link) {
            continue;
        }

        const hasError = paneHasError(pane);
        link.classList.toggle(FORM_TAB_ERROR_CLASS, hasError);
        if (hasError) {
            link.setAttribute('aria-invalid', 'true');
            if (firstErrorLink === null) {
                firstErrorLink = link;
                firstErrorPane = pane;
            }
        } else {
            link.removeAttribute('aria-invalid');
        }
    }

    if (!options.activate || firstErrorLink === null || firstErrorPane === null) {
        return;
    }

    const hidden = form.querySelector<HTMLInputElement>('input[name="selectedTab"]');
    if (hidden && firstErrorPane.id) {
        hidden.value = firstErrorPane.id;
    }

    activateErrorTab(firstErrorLink, firstErrorPane);
}

export function installFormTabErrorHandling(Nette: NetteTabHost, LiveForm: LiveFormTabHost): void {
    let wholeFormValidationDepth = 0;

    const originalValidateForm = Nette.validateForm;
    if (typeof originalValidateForm === 'function') {
        Nette.validateForm = function naisValidateForm(sender: HTMLFormElement | HTMLElement, onlyCheck?: boolean): boolean {
            wholeFormValidationDepth++;
            let valid: boolean;
            try {
                valid = originalValidateForm.call(Nette, sender, onlyCheck);
            } finally {
                wholeFormValidationDepth--;
            }
            const form = (sender as HTMLInputElement).form ?? sender;
            if (form instanceof HTMLFormElement) {
                const activate = !valid && !onlyCheck;
                syncFormTabErrors(form, { activate });
            }
            return valid;
        };
    }

    const originalNetteAddError = Nette.addError;
    if (typeof originalNetteAddError === 'function') {
        Nette.addError = function naisAddNetteError(el: HTMLElement, message: string): void {
            const isLiveValidation = wholeFormValidationDepth === 0
                && !el.classList.contains('no-live-validation');
            if (isLiveValidation && typeof LiveForm.addError === 'function') {
                LiveForm.addError.call(LiveForm, el, message);
                return;
            }
            originalNetteAddError.call(Nette, el, message);
        };
    }

    const originalInitForm = Nette.initForm;
    if (typeof originalInitForm === 'function') {
        Nette.initForm = function naisInitFormTabs(form: HTMLFormElement): void {
            const activateServerErrors = form.dataset.naisFormTabsInitialized !== 'true';
            originalInitForm.call(Nette, form);
            if (!(form instanceof HTMLFormElement)) {
                return;
            }
            form.dataset.naisFormTabsInitialized = 'true';
            syncFormTabErrors(form, { activate: activateServerErrors });
        };
    }

    const originalAddError = LiveForm.addError;
    if (typeof originalAddError === 'function') {
        LiveForm.addError = function naisAddError(el: HTMLElement, message: string): void {
            originalAddError.call(LiveForm, el, message);
            placeSelect2ErrorMessage(el);
            syncControlErrorState(el, true);
            const form = (el as HTMLInputElement).form;
            if (form) {
                syncFormTabErrors(form);
            }
        };
    }

    const originalRemoveError = LiveForm.removeError;
    if (typeof originalRemoveError === 'function') {
        LiveForm.removeError = function naisRemoveError(el: HTMLElement): void {
            originalRemoveError.call(LiveForm, el);
            syncControlErrorState(el, false);
            const form = (el as HTMLInputElement).form;
            if (form) {
                syncFormTabErrors(form);
            }
        };
    }
}
